/* eslint-disable react-refresh/only-export-components */
import { useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "@h2j-data-team/ui";
import { getErrorMessage } from "@/lib/api-client";
import { toast } from "./toast";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  onConfirm: () => void | Promise<void>;
  isPending?: boolean;
  /** Where focus returns on close when no trigger opened the dialog (e.g. a row's menu item). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = "default",
  onConfirm,
  isPending = false,
  restoreFocusRef,
}: ConfirmDialogProps) {
  const { t } = useTranslation("common");
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      // Keep the dialog open so the user can see the error and retry.
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const loading = isPending || isLoading;

  // The library AlertDialog stays open and busy (Cancel and Escape blocked)
  // until the action settles; it closes only on success.
  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
      busy={loading}
      restoreFocusRef={restoreFocusRef}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="outline">{cancelLabel ?? t("cancel")}</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant={variant === "destructive" ? "danger" : "primary"}
              loading={loading}
              onClick={() => void handleConfirm()}
            >
              {confirmLabel ?? t("confirm")}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Hook for easier usage with delete confirmations
export function useConfirmDialog() {
  const [state, setState] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmLabel?: string;
    variant?: "default" | "destructive";
    restoreFocusRef?: RefObject<HTMLElement | null>;
    onConfirm: () => void | Promise<void>;
  }>({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  // Where focus goes back when the confirmation closes: the caller's ref, the
  // element it names, or else the one focused when `confirm` ran.
  const openerRef = useRef<HTMLElement | null>(null);

  const confirm = ({
    restoreFocus,
    ...options
  }: {
    title: string;
    description: string;
    confirmLabel?: string;
    variant?: "default" | "destructive";
    restoreFocusRef?: RefObject<HTMLElement | null>;
    restoreFocus?: HTMLElement | null;
    onConfirm: () => void | Promise<void>;
  }) => {
    openerRef.current = restoreFocus ?? (document.activeElement as HTMLElement | null);
    setState({
      open: true,
      ...options,
    });
  };

  const dialogProps = {
    open: state.open,
    onOpenChange: (open: boolean) => setState((prev) => ({ ...prev, open })),
    title: state.title,
    description: state.description,
    confirmLabel: state.confirmLabel,
    variant: state.variant,
    restoreFocusRef: state.restoreFocusRef ?? openerRef,
    onConfirm: state.onConfirm,
  };

  return { confirm, dialogProps, ConfirmDialog };
}
